import { generateMarketPathname } from '@chewy/market-handler';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SEGMENT_TAGS } from '@/analytics/tag-definitions';
import { TERMS_AND_CONDITIONS_LINK } from '@/components/navigation/constants';
import { useDeviceSupportsNativeScrollingExperience } from '@/hooks/useDeviceSupportsNativeScrollingExperience';
import { useFeatureValue } from '@/hooks/useFeatureValue';
import { useLazySheet } from '@/hooks/useLazySheet';
import { useAnalyticsContext } from '@/providers/AnalyticsProvider';
import { useMarket } from '@/providers/MarketProvider';
import { FeatureConfigKeys } from '@/utils/athena-feature-config';

import styles from './ChewyPlusTerms.module.scss';

const loadTermsAndConditionsSheet = () =>
  import('@/components/navigation/TermsAndConditionsSheet').then(
    mod => mod.TermsAndConditionsSheet,
  );

interface ChewyPlusTermsProps {
  showWebExperience?: boolean;
  label?: string;
  style?: 'center' | 'inline';
  analyticsOverrideCallback?: () => void;
}

export const ChewyPlusTerms = ({
  showWebExperience = false,
  label,
  style,
  analyticsOverrideCallback,
}: ChewyPlusTermsProps) => {
  const { t } = useTranslation();
  const { market } = useMarket();
  const { trackSegmentEvent } = useAnalyticsContext();
  const [showTermsSheet, setShowTermsSheet] = useState(false);

  const termsAndConditionsKibSheetEnabled = useFeatureValue(
    FeatureConfigKeys.TERMS_AND_CONDITIONS_KIB_SHEET_ENABLED,
    false,
  );
  const termsAndConditionsNativeSheetEnabled = useFeatureValue(
    FeatureConfigKeys.TERMS_AND_CONDITIONS_NATIVE_SHEET_ENABLED,
    false,
  );

  const deviceSupportsNativeExperience = useDeviceSupportsNativeScrollingExperience();

  const showNativeSheetExperience =
    termsAndConditionsNativeSheetEnabled && !showWebExperience && deviceSupportsNativeExperience;
  const showKibSheetExperience = termsAndConditionsKibSheetEnabled && !showNativeSheetExperience;
  const TermsAndConditionsSheet = useLazySheet(loadTermsAndConditionsSheet, {
    prefetch: showKibSheetExperience,
  });

  const handleAnalytics = useCallback(() => {
    if (analyticsOverrideCallback) {
      analyticsOverrideCallback();
    } else {
      trackSegmentEvent(
        SEGMENT_TAGS.TakenToChewyPlusTerms.eventName,
        SEGMENT_TAGS.TakenToChewyPlusTerms.data,
      );
    }
  }, [analyticsOverrideCallback, trackSegmentEvent]);

  const handleTermsClick = useCallback(
    (event: React.MouseEvent) => {
      handleAnalytics();

      if (showKibSheetExperience) {
        event.preventDefault();
        setShowTermsSheet(true);
      }
    },
    [handleAnalytics, showKibSheetExperience],
  );

  const handleCloseSheet = useCallback(() => {
    setShowTermsSheet(false);
  }, []);

  return (
    <>
      <span className={styles[style ?? 'center']}>
        <span className={styles.chewyPlusTermsText}>
          {showKibSheetExperience ? (
            <button
              aria-controls="terms-and-conditions-sheet"
              aria-expanded={showTermsSheet}
              aria-label={t('loyalty.membership.footer.chewy-plus-terms')}
              className={styles.termsButton}
              onClick={handleTermsClick}
              type="button"
            >
              {label ?? t('loyalty.membership.footer.chewy-plus-terms')}
            </button>
          ) : (
            <a
              aria-label={t('loyalty.membership.footer.chewy-plus-terms')}
              className={styles.termsButton}
              href={generateMarketPathname(TERMS_AND_CONDITIONS_LINK, market)}
              onClick={handleTermsClick}
              rel="noopener"
              target={showWebExperience ? '_blank' : undefined}
            >
              {label ?? t('loyalty.membership.footer.chewy-plus-terms')}
            </a>
          )}
        </span>
      </span>
      {showKibSheetExperience && showTermsSheet && (
        <TermsAndConditionsSheet handleClose={handleCloseSheet} showSheet={showTermsSheet} />
      )}
    </>
  );
};
